import { Theme } from '@material-ui/core/styles/createTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  targetColor,
  depositColor,
  reserveColor,
  dateColor,
  altColor,
  failedDepositColor,
  extProdColor,
  loanColor,
} from './Chart';
import { RANGE_HEIGHT } from './Chart';
import { EventLabelProps } from './model';
import { borderRadiusBox } from 'components/AppTheme/index';
import { EdwardColors } from 'enums';

export interface RangeStyling {
  margin?: number;
  lineColor?: string;
  activeAreaRGB?: string;
  sliderColor?: string;
}

interface Pdf {
  pdf?: boolean;
  overview?: boolean;
  styling?: RangeStyling;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fixChart: (props: Partial<EventLabelProps> & Pdf) => ({
      marginBottom: 0,
      '& .highcharts-tooltip path': {
        display: 'none',
      },
      '& .highcharts-plot-line': {
        [theme.breakpoints.down('xs')]: {
          display: 'none',
        },
      },
      '& .highcharts-plot-line-label': {
        [theme.breakpoints.down('xs')]: {
          display: 'none',
        },
      },
      '& ._tooltip': {
        display: 'block',
        width: '100%',
        zIndex: 100,
        '& > span, & > span > span': {
          display: 'block',
          '@media (max-width: 400px)': {
            width: `calc(100% + ${theme.spacing(2)}px)`,
          },
        },
        '& > span > span': {
          background: '#f8f8f8',
          boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
          borderRadius: borderRadiusBox,
          padding: theme.spacing(1),
          position: 'relative',
          top: '50px',
          [theme.breakpoints.down('xs')]: {
            top: '154px',
          },
        },
        '& table': {
          margin: '0 auto',
          fontFamily: 'Roboto, sans-serif',
          borderCollapse: 'collapse',
          '& ._dot': {
            fontSize: '36px',
            lineHeight: '16px',
          },
          '& td': {
            lineHeight: '14px',
            padding: '2px 6px',
            verticalAlign: 'middle',
            '@media (max-width: 400px)': {
              padding: '2px',
            },
          },
          '& ._age': {
            fontWeight: 'bold',
            '& td:nth-child(2)': { textAlign: 'center' },
          },
        },
      },
      '& .noTooltip ._tooltip, & .noTooltip .highcharts-crosshair': {
        display: `none !important`,
      },
      '& .highcharts-legend-item rect:before': {
        //visibility: 'hidden'
      },
      '& .highcharts-legend-item > span': {
        overflow: `visible !important`,
        '& > span': {
          display: 'block',
          paddingTop: '4px',
        },
      },
      '& .highcharts-legend-item > span:before': props.pdf
        ? undefined
        : {
            content: '""',
            display: 'inline-block',
            width: '12px',
            height: '2px',
            background: '#fff',
            position: 'absolute',
            left: '-18px',
            top: '3px',
            transform: 'rotate(-45deg)',
          },
      '& .highcharts-legend-item > span:after': props.pdf
        ? undefined
        : {
            content: '""',
            display: 'inline-block',
            width: '7px',
            height: '2px',
            background: '#fff',
            position: 'absolute',
            left: '-21px',
            top: '5px',
            transform: 'rotate(45deg)',
          },
      '& .highcharts-legend-item-hidden  > span:before': props.pdf
        ? undefined
        : {
            background: '#fff',
            content: '""',
            top: '-3px',
            left: '-21px',
            width: '14px',
            height: '14px',
            transform: 'none',
          },
      '& .highcharts-legend-item-hidden  > span:after': {
        display: 'none',
      },
    }),
    hover: (props: Partial<EventLabelProps> & Pdf) => ({
      '&:hover': props.pdf
        ? undefined
        : {
            zIndex: 1000,
          },
    }),
    graphInfo: (props: Partial<EventLabelProps> & Pdf) => ({
      [theme.breakpoints.down('xs')]: {
        textAlign: 'left',
      },
      '& .MuiTypography-body1': {
        fontSize: '14px',
      },
    }),
    value: {
      fontSize: '28px',
      fontWeight: 900,
    },

    label: (props: Partial<EventLabelProps> & Pdf) => {
      const markerSize = Math.max(Math.floor((props.size || 1) / 7), 6) - (props.pdf ? 1 : 0);
      const borderSize = Math.floor(Math.max((props.size || 1) / 15, 2)); // 2, 3, 4
      const labelColor = props.color
        ? props.color
        : props.altColors
        ? altColor
        : props.type === 'TARGET'
        ? targetColor
        : props.type === 'PROPERTY'
        ? extProdColor
        : props.type === 'LOAN'
        ? loanColor
        : props.type === 'RESERVE'
        ? reserveColor
        : props.type === 'DATE'
        ? dateColor
        : props.type === 'WITHDRAWAL'
        ? EdwardColors.WARNING
        : props.failedDeposit === true
        ? failedDepositColor
        : depositColor;
      return {
        position: 'relative',
        cursor: props.readOnly ? 'default' : 'pointer',
        fontFamily: 'Roboto',
        display: 'block',
        width: `${props.size}px`,
        height: `${props.size}px`,
        background: props.historical ? 'transparent' : props.type === 'LOAN' ? labelColor : props.color && props.type !== 'PROPERTY' ? props.color : '#fff',
        boxShadow: props.historical ? '0 0 10px 1px rgba(0,0,0,0.13)' : 'none',
        borderStyle: props.historical || props.type === 'LOAN' ? 'none' : 'solid',
        borderColor: labelColor,
        borderWidth: props.type === 'LOAN' ? 0 : `${borderSize}px`,
        borderRadius: '50%',
        fontSize: `${props.size! - props.size! / 6 - 4}px`,
        filter: props.altColors ? 'grayscale(100%)' : undefined,
        '& > strong ': {
          overflow: 'hidden',
          display: 'inline-block',
          width: 'calc(100%)',
          height: 'calc(100%)',
          borderRadius: '50%',
          '& img': {
            width: props.color ? '100%' : '90%',
            height: props.color ? '100%' : '90%',
            left: props.color ? '0%' : '5%',
            top: props.color ? '0%' : '5%',
            position: 'relative',
          },
        },
        // pointer || translucent marker (historical)
        '&:before':
          props.type === 'LOAN' ? undefined :
          props.historical
            ? {
                display: 'block',
                width: '100%',
                height: '100%',
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                borderRadius: '50%',
                backgroundColor: labelColor,
                opacity: 0.7,
              }
            : {
                display: props.repeat && props.type === 'RESERVE' ? 'none' : 'inline-block',
                content: props.type === 'PERIODIC_DEPOSIT' ? '' : `""`,
                border: `solid transparent ${markerSize}px`,
                borderLeftColor:
                  !props.repeat || props.type === 'RESERVE' // repeating
                    ? 'transparent'
                    : props.altColors
                    ? altColor
                    : props.color
                    ? props.color
                    : props.type === 'TARGET'
                    ? targetColor
                    : depositColor,
                borderRightColor:
                  props.type === 'RESERVE' && props.altColors // reserve
                    ? altColor
                    : props.type === 'RESERVE'
                    ? reserveColor
                    : 'transparent',
                borderTopColor:
                  props.repeat || props.type === 'RESERVE' // single
                    ? 'transparent'
                    : props.altColors
                    ? altColor
                    : props.color
                    ? props.color
                    : props.type === 'TARGET'
                    ? targetColor
                    : props.type === 'PROPERTY'
                    ? extProdColor
                    : props.type === 'DATE'
                    ? dateColor
                    : depositColor,
                position: 'absolute',
                right: !props.repeat || props.type === 'RESERVE' ? undefined : `-${markerSize * 2 + borderSize - 1}px`,
                left:
                  props.type === 'RESERVE'
                    ? `-${markerSize * 2 + borderSize - 1}px`
                    : !props.repeat
                    ? '50%'
                    : undefined,
                top: props.repeat || props.type === 'RESERVE' ? '50%' : undefined,
                bottom: !props.repeat && props.type !== 'RESERVE' ? `-${markerSize * 2 + borderSize / 2}px` : undefined,
                transform: props.repeat || props.type === 'RESERVE' ? 'translate(0,-50%)' : 'translate(-50%,0)',
              },
        // historical dot
        '&:after':
          props.historical
            ? {
                display: 'block',
                content: '""',
                borderRadius: '50%',
                background: labelColor,
                width: '12px',
                height: '12px',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
                opacity: 1,
              }
            : undefined,
        // dot
        '& em:first-child': props.type === 'LOAN' ? undefined : {
          display: 'inline-block',
          borderRadius: '50%',
          width: props.pdf ? '8px' : '10px',
          height: props.pdf ? '8px' : '10px',
          background: labelColor,

          position: 'absolute',
          left: `calc(50% - ${props.type === 'PERIODIC_DEPOSIT' ? 10 : props.pdf ? 4 : 5}px)`,
          top: `calc(100% + ${props.type === 'PERIODIC_DEPOSIT' ? 3 : 8}px)`,

          lineHeight: '20px',
          textAlign: 'center',
          fontSize: '20px',
          fontStyle: 'normal',
          fontWeight: 'bold',
          color: 'rgba(0,0,0,0.2)',

          '&.hidden': { display: 'none' },
        },
        // line
        '& em:nth-child(2)': props.type === 'LOAN' ? undefined : {
          display: 'inline-block',
          left: '50%',
          bottom: '0',
          transform: 'translate(-1px,100%)',
          position: 'absolute',
          borderRightStyle: 'dashed',
          borderRightWidth: '2px',
          borderRightColor: labelColor,
          opacity: 0.4,
          zIndex: -1,
        },

        '& svg': {
          position: 'relative',
          left: props.pdf ? '2px' : '3px',
          top: props.pdf ? '2px' : '3px',
        },

        '&:hover': props.pdf
          ? undefined
          : {
              borderColor: '#666',
              '&:before': {
                borderLeftColor: !props.repeat || props.type === 'RESERVE' ? undefined : EdwardColors.GRAY_MEDIUM,
                borderRightColor: props.type === 'RESERVE' ? EdwardColors.GRAY_MEDIUM : undefined,
                borderTopColor: !props.repeat && props.type !== 'RESERVE' ? EdwardColors.GRAY_MEDIUM : undefined,
              },
              '& em:first-child, & em:first-child.hidden': {
                background: EdwardColors.GRAY_MEDIUM,
              },
              '& em:nth-child(2)': {
                display: 'inline-block',
                borderRightStyle: 'solid',
                borderRightColor: EdwardColors.GRAY_MEDIUM,
                opacity: 1,
              },
              '& > span': {
                visibility: 'visible',
              },
            },
        // details info box
        '& > span': {
          visibility: 'hidden',
          position: 'absolute',
          left: '50%',
          top: 0,
          transform:
            props.align === 'center'
              ? 'translate(-50%,-115%)'
              : props.align === 'left'
              ? 'translate(-5%,-115%)'
              : 'translate(-95%,-115%)',
          padding: theme.spacing(1),
          lineHeight: '1.3',
          background: '#f8f8f8',
          borderRadius: borderRadiusBox,
          fontSize: '12px',
          boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
          '&:after': {
            display: 'inline-block',
            content: '""',
            border: 'solid transparent 9px',
            borderTopColor: '#ccc',
            position: 'absolute',
            left: props.align === 'center' ? '50%' : props.align === 'left' ? '0' : '100%',
            transform:
              props.align === 'center' ? 'translate(-50%, 0)' : props.align === 'left' ? 'none' : 'translate(-100%, 0)',
          },
        },
        '&[data-tooltip-overflow="false"] > span:after': {
          bottom: '-18px',
        },
        '&[data-tooltip-overflow="true"] > span': {
          transform:
            props.align === 'center'
              ? 'translate(-50%,85%)'
              : props.align === 'left'
              ? 'translate(-5%,85%)'
              : 'translate(-95%,85%)',
          '&:after': {
            top: '-18px',
            borderTopColor: 'transparent',
            borderBottomColor: '#ccc',
          },
        },
      };
    },

    dateIcon: {
      display: 'flex',
      width: '36px',
      height: '36px',
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(1),
      borderStyle: 'solid',
      borderWidth: '2px',
      borderColor: dateColor,
      borderRadius: '50%',
      fontSize: '32px',
      '& > svg': {
        borderRadius: '50%',
      },
    },
    title: (props: Partial<EventLabelProps> & Pdf) => ({
      padding: props.overview ? theme.spacing(1) : theme.spacing(0, 0, 0, 1),
      marginBottom: 0,
    }),
    chartRange: {
      justifyContent: 'center',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        /*
        '& .MuiTypography-body1': {
          lineHeight: '40px',
        },
        */
      },
      '@media (max-width: 520px)': {
        '& .MuiGrid-root .MuiGrid-grid-xs-6': {
          flexBasis: '100%',
          maxWidth: '100%',
        },
      },
    },
    rangeWrapper: (props: Partial<EventLabelProps> & Pdf & RangeStyling) => ({
      marginTop: theme.spacing(props.styling?.margin !== undefined ? props.styling?.margin : -2),
    }),
    rangeSlider: (props: Partial<EventLabelProps> & Pdf & RangeStyling) => ({
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(6),
      padding: 0,
      color: props.styling?.sliderColor,

      '& .MuiSlider-rail, & .MuiSlider-track': { height: '4px', marginTop: '-1px' },
      '& .MuiSlider-rail:after, & .MuiSlider-track:after': {
        display: 'inline-block',
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        top: `-${RANGE_HEIGHT + 10}px`,
        bottom: '4px',
      },
      '& .MuiSlider-track:after': {
        border: `solid rgba(${props.styling?.activeAreaRGB || '255, 221, 127'}, 0.66) 1px`,
        borderBottom: 'none',
        background: `rgba(${props.styling?.activeAreaRGB || '255, 221, 127'}, 0.33)`,
      },
      '& .MuiSlider-valueLabel': {
        left: 'calc(50% - 80px)',
        background: EdwardColors.MEDIUM,
        borderRadius: '6px',
        padding: '3px',
      },
      /*
      '& .MuiSlider-thumb:before': {
        position: 'absolute',
        left: '50%',
        transform: `translate(-50%, -${RANGE_HEIGHT-4}px )`,
        color: '#666',
        border: 'solid #666 1px',
        content: '""',
      },
      */
      '& .MuiSlider-thumb > span > span': {
        width: '80px',
        color: 'transparent',
      },
      '& .MuiSlider-markLabel': {
        fontSize: '11px',
      },
    }),
  }),
);

export default useStyles;
